import React, { useCallback, useState, useEffect } from "react";
import "../Cassette.scss";
import DataTableSubHeader from "../../../Components/DataTableSubHeader/DataTableSubHeader";
import { useLocation, useNavigate } from "react-router-dom";
import { AddIcon, CrossIcon } from "../../../assets/icons";
import { SubHeaderMobileOption } from "../../../Components/SubHeaderMobileOption/SubHeaderMobileOption";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { createCassetta, createRaccolta, getDepositi, saveCassettaImg } from "../../../api";
import { v4 as uuidv4 } from "uuid";
import validateCassetta from "../../../helpers/formTools/validations/cassetta/validateCassetta";
import validateRaccolte from "../../../helpers/formTools/validations/raccolte/validateRaccolte";
import { sanitizeProvenienze } from "../../../helpers/utils/utils";
import { useAppContext } from "../../../Components/Context/AppContext";
import CassetteRecordDetailForm from "../../../Components/Cassette/AddCassetteRecordDetailForm/AddCassetteRecordDetailForm";
import useFetchData from "../../../hooks/useFetchData";
import { useCheckRole } from "../../../hooks/useCheckRole";

function AddCassetta() {
  const { comuni, setCassetteFilter } = useAppContext();
  const [isMenuActive, setMenuActive] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({});
  const [issues, setIssues] = useState([]);
  const [showIissues, setShowIssues] = useState(false);
  const [raccolteData, setRaccolteData] = useState([{ id: uuidv4() }]);
  const { data: depositiData } = useFetchData(getDepositi);
  const [immagini, setImmagini] = useState([]);

  //********************************************************** */
  // EFFECTS
  useCheckRole("CASSETTA_WRITE");

  useEffect(() => {
    let issues = validateCassetta(formData);
    issues = issues.concat(validateRaccolte(raccolteData));
    setIssues(issues);
  }, [formData, raccolteData]);

  //************************************************************************** */
  // API CALLS

  const newCassetta = async (data) => {
    try {
      const createdCassetta = await createCassetta(data);
      toast.success("Cassetta creata con successo!");
      return createdCassetta;
    } catch (error) {
      if (error?.status === 401) {
        navigate("/");
        console.error(`Authorization error saving a new "cassetta"`);
      } else {
        toast.error(
          error?.data?.detail ?? error?.detail ?? `Unknown error saving a new "cassetta"`
        );
        console.error(`Error saving a new "cassetta"`, error);
      }
      return null;
    }
  };

  const newRaccolta = async (data) => {
    try {
      await createRaccolta(data);
      // toast.success("Data updated successfully!");  // Reducing the number of toasts
    } catch (error) {
      if (error.response.status === 401) {
        navigate("/");
        console.error(`Authorization error saving a new "raccolta"`, data);
      } else {
        toast.error(
          error.response && error.response.data && error.response.data.detail
            ? error.response.data.detail
            : `Unknown error saving a new "raccolta"`
        );
        console.error(`Unknown error saving a new "raccolta"`, error);
      }
    }
  };

  // ************************************************
  // ALL HANDLERS

  const handleSaveImageRef = async (cassettaId) => {
    try {
      if (!immagini || immagini.length === 0) return;
      const submitData = immagini
        .map((img) => ({
          immagineId: img.immagineId,
          cassettaId: cassettaId,
        }));

      console.log(
        //"===| Saving immagini...",
        submitData,
        "--->>>",
        formData.immagini
      );

      await Promise.all(submitData.map((img) => saveCassettaImg(img)));
      toast.success("Immagini salvate con successo!");
    } catch (err) {
      console.log(err);
      toast.error("Errore nel salvataggio delle immagini");
    }
  };


  const handleSave = async () => {
    if (issues.length > 0) {
      setShowIssues(true);
      return;
    }

    const cassetta = {
      ...formData,
      comuneId: raccolteData[0]?.provenienze?.[0]?.comuneId ?? '02b567fd-f8ed-4b89-bd97-bef4acffab72',
      groups: formData.groups.map((group) => group.id), // Convert groups to array of ids
    };

    const created = await newCassetta(cassetta);
    if (!created?.id) return;

    const cassettaId = created.id;
    await handleSaveImageRef(cassettaId);
    await Promise.all(
      raccolteData.map(async (raccolta) => {
        raccolta.cassettaId = cassettaId;
        const sumbitData = {
          ...raccolta,
          descrizione: Array.isArray(raccolta.descrizione)
            ? raccolta.descrizione.reduce((acc, curr) => {
                acc[curr.class] = curr.quantity;
                return acc;
              }, {})
            : raccolta.descrizione,
          provenienze: sanitizeProvenienze(raccolta.provenienze),
        };
        await newRaccolta(sumbitData);
      })
    );
    setCassetteFilter([]); // Reset the filter
    navigate(-1);
  };

  const handleFormDataChange = (key, newValue) => {
    setFormData((prevData) => ({
      ...prevData,
      [key]: newValue,
    }));
  };

  const handleRaccoltaChange = (id, key, newValue) => {
    setRaccolteData((prevData) =>
      prevData.map((item, i) =>
        item.id === id ? { ...item, [key]: newValue } : item
      )
    );
  };

  const handleAddRaccolta = () => {
    setRaccolteData((prevData) => [...prevData, { id: uuidv4() }]);
    toast.success("Raccolta aggiunta con successo`!");
  };

  const handleDeleteRaccolta = (raccolta) => {
    setRaccolteData((prevData) =>
      prevData.filter((item) => item.id !== raccolta.id)
    );
    toast.success("Raccolta cancellata con successo`!");
  };

  const handleChangeImmagini = useCallback((val) => {
    setImmagini(val);
  }, []);

  const disabled = pathname !== "/cassette/edit";
  const cassetteRecordDetailsBreadcrumbs = [
    {
      id: 1,
      label: "Dati Archeoligici",
      iconUrl: true,
    },
    {
      id: 2,
      label: "CASSETTE",
      iconUrl: true,
    },
    { id: 3, label: "Dettaglio cassetta", iconUrl: "" },
  ];

  const subHeaderRightNavOptions = {
    icons: [],
    components: [
      <button onClick={() => navigate(-1)} className="cancel-button">
        <CrossIcon />
        <span className="login-text">Annulla</span>
      </button>,
      <button
        onClick={() => {
          //          navigate(-1);
          handleSave();
        }}
        className="save-button"
      >
        <AddIcon fillColor="#fff" />
        <span className="login-text">Salva</span>
      </button>,
    ],
  };

  return (
    <>
      <div className="container">
        <main className="record-detail-main-content">
          <DataTableSubHeader
            title={"Dettaglio cassetta"}
            breadcrumbsData={cassetteRecordDetailsBreadcrumbs}
            subHeaderRightNavOptions={subHeaderRightNavOptions}
            isMenuActive={isMenuActive}
            setMenuActive={() => setMenuActive(!isMenuActive)}
          />
          <CassetteRecordDetailForm
            formData={formData}
            raccolteData={raccolteData}
            depositiData={depositiData}
            handleAddRaccolta={handleAddRaccolta}
            handleDeleteRaccolta={handleDeleteRaccolta}
            handleFormDataChange={handleFormDataChange}
            handleRaccoltaChange={handleRaccoltaChange}
            handleImageChange={handleChangeImmagini}
            immagini={immagini}
            disabled={disabled}
            issues={issues}
            showIssues={showIissues}
          />
        </main>
        {isMenuActive && (
          <SubHeaderMobileOption
            buttons={subHeaderRightNavOptions?.components}
            icons={subHeaderRightNavOptions?.icons}
            onCrossClick={() => setMenuActive(false)}
            title="options"
          />
        )}
      </div>
    </>
  );
}

export default AddCassetta;
